// BusinessPage — the contractor's public page, used as their "business website"
// for toll-free verification.
//
// WHY THIS PAGE EXISTS
// Toll-free verification requires a public business website, and rejects
// submissions where the URL cannot be validated (30473) or does not visibly
// carry the business's own branding (30492). A large share of trade contractors
// have no website at all — Twilio accepts a social page, but plenty do not have
// one of those either. This page fills that gap.
//
// THE CONSTRAINT THAT SHAPES EVERY DESIGN CHOICE HERE
// It must read as THE CONTRACTOR'S page, not as a TradesMatePro directory
// listing. Twilio rejects third-party platform URLs without clear business
// branding (30492), and rejects opt-in evidence showing the ISV's branding
// rather than the end business's (30506). So: their name is the headline, their
// logo, their colours, their contact details, and the opt-in disclosure names
// THEM. Our branding is a single small line in the footer and nothing more.
// Do not add nav, cross-links to other companies, marketplace CTAs, or anything
// else that makes this look like a platform page — it would fail verification
// for every contractor using it.
//
// Keyed on companyId, matching the existing /store/:companyId convention rather
// than introducing a slug column.

import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '../utils/supabaseClient';
import { documentSecondaryColor } from '@tradesmatepro/shared-ui';

export default function BusinessPage() {
  const { companyId } = useParams();
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Via RPC, not a table read. `companies` has no anon SELECT policy and
        // company_sms_compliance is tenant-scoped, so a direct .from() query
        // returns nothing for a logged-out visitor — which is every visitor this
        // page is for. The function returns only the public fields and only when
        // the contractor has switched the page on.
        const { data, error } = await supabase.rpc('get_public_business_page', {
          p_company_id: companyId
        });
        if (cancelled) return;
        if (error) throw error;
        setCompany(Array.isArray(data) ? data[0] ?? null : data ?? null);
      } catch (err) {
        console.error('BusinessPage load failed:', err);
        if (!cancelled) setCompany(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [companyId]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-slate-400 text-sm">Loading…</div>
      </div>
    );
  }

  // 404 cleanly for companies that have not opted in — the RPC returns no row
  // in that case. A page that renders an empty shell is worse than no page:
  // Twilio would reject it as an unvalidatable URL (30473) and the contractor
  // would not know why.
  if (!company) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white px-6">
        <div className="text-center">
          <h1 className="text-xl font-semibold text-slate-900">Page not found</h1>
          <p className="mt-2 text-sm text-slate-500">
            This business page isn't available.
          </p>
        </div>
      </div>
    );
  }

  const displayName = company.legal_name || company.name;
  const tradingName = company.dba_name || company.name;
  const logo = company.logo_url;
  // Both of these arrive already COALESCEd over the legacy shadow columns by
  // get_public_business_page — do not re-derive them here.
  const banner = company.banner_url;
  const accent = company.theme_color || '#1e293b';
  // companies.secondary_color (operator ruling 2026-10-29): the accent rule under the header and
  // the contact links, using the contrast-checked text shade (>= 4.5:1 on white). Not picked
  // (the column default) = null, and the page looks exactly as before.
  const second = documentSecondaryColor(company.secondary_color);
  const linkStyle = second ? { color: second.accentText } : undefined;

  const addressLines = [
    company.address_line1,
    company.address_line2,
    [company.city, company.state_province, company.postal_code].filter(Boolean).join(', ')
  ].filter(Boolean);

  return (
    <div className="min-h-screen bg-white text-slate-900">
      {/* Cover image, above the header rather than inside it, so it spans the full width the
          way a business site's masthead does. It is the CONTRACTOR'S own image, which is
          what this page needs more of: verification rejects a URL that does not visibly
          carry the business's own branding (30492). Renders nothing when unset, leaving the
          existing logo-and-name header exactly as it was. */}
      {banner && (
        <img
          src={banner}
          alt={`${tradingName} banner`}
          className="w-full h-40 sm:h-56 object-cover"
        />
      )}
      <header
        className="border-b border-slate-200"
        style={second ? { borderBottom: `3px solid ${second.accent}` } : undefined}
      >
        <div className="max-w-3xl mx-auto px-6 py-10">
          {logo && (
            <img
              src={logo}
              alt={`${tradingName} logo`}
              className="h-16 w-auto mb-6 object-contain"
            />
          )}
          <h1 className="text-3xl font-bold tracking-tight" style={{ color: accent }}>
            {tradingName}
          </h1>
          {/* Naming the legal entity behind a trading name is what closes error
              30484 — a business name on the website that does not match the
              registered name is a rejection unless the DBA is disclosed. */}
          {company.dba_name && displayName !== company.dba_name && (
            <p className="mt-1 text-sm text-slate-500">
              {company.dba_name} is a trading name of {displayName}.
            </p>
          )}
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-10 space-y-10">
        {company.profile_description && (
          <section>
            <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
              About
            </h2>
            <p className="mt-3 text-slate-700 leading-relaxed">
              {company.profile_description}
            </p>
          </section>
        )}

        <section>
          <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
            Contact
          </h2>
          <div className="mt-3 space-y-1 text-slate-700">
            {company.phone && (
              <p>
                <a href={`tel:${company.phone}`} className="hover:underline" style={linkStyle}>{company.phone}</a>
              </p>
            )}
            {company.email && (
              <p>
                <a href={`mailto:${company.email}`} className="hover:underline" style={linkStyle}>{company.email}</a>
              </p>
            )}
            {company.website && (
              <p>
                <a
                  href={company.website}
                  className="hover:underline"
                  style={linkStyle}
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  {company.website}
                </a>
              </p>
            )}
            {addressLines.length > 0 && (
              <address className="not-italic pt-2">
                {addressLines.map((line) => <div key={line}>{line}</div>)}
              </address>
            )}
          </div>
        </section>

        {/* THE OPT-IN DISCLOSURE — the reason this page can be submitted as
            verification evidence. It names the CONTRACTOR's business, never
            ours (30506), states what messages are sent, and carries the
            STOP/HELP and rates language carriers look for. Changing the wording
            to mention TradesMatePro would invalidate it for every contractor. */}
        <section className="rounded-lg border border-slate-200 bg-slate-50 p-6">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
            Text messages from {tradingName}
          </h2>
          <div className="mt-3 space-y-3 text-sm text-slate-700 leading-relaxed">
            <p>
              {company.opt_in_description ||
                `When you request work or book an appointment with ${tradingName}, ` +
                `you can give us your mobile number and agree to receive text messages ` +
                `about your job.`}
            </p>
            <p>
              We send appointment reminders, quotes, invoices and updates about work in
              progress. We do not sell or share your number, and we do not send marketing
              texts.
            </p>
            <p>
              Message and data rates may apply. Message frequency varies. Reply STOP to
              stop receiving texts, or HELP for help.
            </p>
          </div>
        </section>

        <section className="text-sm text-slate-500 space-x-4">
          <a href={`/business/${companyId}/terms`} className="hover:underline">Terms</a>
          <a href={`/business/${companyId}/privacy`} className="hover:underline">Privacy</a>
        </section>
      </main>

      {/* Our attribution is deliberately this small and this plain. Anything
          more prominent risks the page reading as a platform listing. */}
      <footer className="border-t border-slate-200">
        <div className="max-w-3xl mx-auto px-6 py-6 text-xs text-slate-400">
          Scheduling and billing powered by TradesMatePro.
        </div>
      </footer>
    </div>
  );
}
